import { PageSection, PageSectionVariants, DataList, Toolbar, ToolbarContent, ToolbarItem, Button, Modal, ModalVariant, Alert, EmptyState, EmptyStateBody, Tab, TabTitleText, Tabs, ToolbarGroup, EmptyStateHeader, Tooltip } from '@patternfly/react-core';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isString } from "lodash-es";
import { SaveIcon, UndoIcon, ExclamationCircleIcon } from '@patternfly/react-icons'

import './model-selection-page.css';

import { CustomizationBase, CustomizationBaseWithModelName, CustomizationObjectType, Model, ModelSelectionScope, ModelType } from '../../store/global-types/customization-types';
import { Trans, useTranslation } from 'react-i18next';
import { hasUserName } from '../../store/configurationTarget/mvision-client-list';
import { appConfigSelectors } from '../../store/appConfig/appConfigSlice';
import { configurationTargetSelectors } from '../../store/configurationTarget/configurationTargetSlice';
import { StoreState } from '../../store/store';
import CustomizationFilters from '../customization/CustomizationFilters';
import ContourSelectionRules from './contour/ContourSelectionRules';
import DoseSelectionRules from './dose/DoseSelectionRules';
import CustomizationSearchBar from '../customization/CustomizationSearchBar';
import { VersionTags } from '../../components/VersionTags';
import { localeIncludes } from '../../util/string-search';
import { getActiveFreeTextFilter, getFreeTextFilterAction } from '../../store/global-types/filter-helpers';
import { ConcurrentSaveError } from '../../util/errors';
import { useConcurrentSaveError } from '../../hooks/useConcurrentSaveError';
import { ConcurrentSaveErrorAlert } from '../../components/ConcurrentSaveErrorAlert';


const getHeadingForModelRules = (customizationId: string): string => {
    return getHeadingForModelName(customizationId);
}

const getHeadingForModelName = (customizationId: string): string => {
    return `heading-modelRules-${customizationId}`;
}

/** Scrolls viewport to given heading, if it exists on the page.
 * @param heading The heading to scroll to. Must match to an id attribute
 * in a DOM element.
 */
const jumpToHeading = (heading: string) => {
    const domElement = document.querySelector(`#${heading}`);
    if (domElement) {
        domElement.scrollIntoView({ behavior: 'smooth' });
    }
}
interface GetTabParams {
    label: string;
    customization: CustomizationBase | undefined;
    /** Customization-base ids carrying a derived (form-validation) blocking error somewhere in
     *  their subtree. */
    customizationBaseIdsWithFormValidationErrors: ReadonlySet<string>;
    /** Customization-base ids carrying a backend validation error keyed to a descendant entity
     *  (e.g. from a failed save round-trip). */
    customizationBaseIdsWithBackendValidationErrors: ReadonlySet<string>;
    modelEntities: Record<string, { version?: string; isLatest?: boolean }>;
    activeVersionTags: string[];
}

const getTab = ({ label, customization, customizationBaseIdsWithFormValidationErrors, customizationBaseIdsWithBackendValidationErrors, modelEntities, activeVersionTags }: GetTabParams) => {
    if (customization === undefined) {
        throw new Error(`Customization with id ${label} not found`);
    }

    const { isModified } = customization;
    const hasValidationError =
        customizationBaseIdsWithFormValidationErrors.has(customization.id)
        || customizationBaseIdsWithBackendValidationErrors.has(customization.id);

    // Get model information for version display
    const model = modelEntities[customization.modelId];
    const version = model?.version;
    const isLatest = model?.isLatest;

    // show item label with modification indicator, but don't show tags for customizations
    const title: ReactNode = isModified ? `${label}*` : label;

    const className = isModified ? 'model-selection-nav-modified' : '';

    const titleComponent = (
        <span className={`${hasValidationError ? 'validation-error' : ''}`}>
            {hasValidationError && (
                <span className="validation-error-icon">
                    <ExclamationCircleIcon />
                </span>
            )}
            <span style={{ marginRight: '8px' }}>{title}</span>
            <span style={{ marginLeft: '4px' }}>
                <VersionTags 
                    version={version} 
                    isLatest={isLatest} 
                    activeTags={activeVersionTags}
                />
            </span>
        </span>
    );

    return (
        <Tab
            key={customization.id}
            eventKey={customization.id}
            title={<TabTitleText className={className}>{titleComponent}</TabTitleText>}
            onSelect={() => jumpToHeading(getHeadingForModelName(customization.id))}
        />
    );
}

const getUndoModalText = (undoScope: ModelSelectionScope) => {
    switch (undoScope) {
        case ModelSelectionScope.All:
            return <Trans i18nKey="modelSelection.undo.text.all">
                Do you want to undo all changes to model selection and revert the entire model selection back to previously saved configuration?
            </Trans>;
        case ModelSelectionScope.ModelRules:
            return <Trans i18nKey="modelSelection.undo.text.modelRules">
                Do you want to undo all selection-related changes for this one model and revert them back to previously saved configuration? Any changes to other models are kept.
            </Trans>;
        case ModelSelectionScope.AeTitleRule:
            return <Trans i18nKey="modelSelection.undo.text.aeTitleRule">
                Do you want to undo changes to this AE Title rule and revert it back to previously saved configuration?
            </Trans>;
        case ModelSelectionScope.DicomRule:
            return <Trans i18nKey="modelSelection.undo.text.dicomRule">
                Do you want to undo changes to this DICOM rule and revert it back to previously saved configuration?
            </Trans>;
        case ModelSelectionScope.DicomAttributeRule:
            return <Trans i18nKey="modelSelection.undo.text.dicomAttributeRule">
                Do you want to undo changes to this DICOM rule attribute condition and revert it back to previously saved configuration?
            </Trans>;
        case ModelSelectionScope.None:
        default:
            return <Trans i18nKey="modelSelection.undo.text.default">
                Do you want to undo all changes and revert them back to previously saved configuration? <strong>Note: this operation will be performed both for your selection and customization changes!</strong>
            </Trans>;
    }
}

export type ModelSelectionUndoState = {
    isModalOpen: boolean,
    scope: ModelSelectionScope,
    id?: string | undefined,
}

export type ModelSelectionPageProps = {
    modelType: ModelType,
    customizationBaseIdsInOrder: CustomizationBaseWithModelName[],
    customizationBaseEntities: Record<string, CustomizationBase>,
    activeVersionTags: string[],
    modelEntities: Record<string, Model>,
    isSavingInProgress: boolean,
    isAnyCustomizationModelModified: boolean,
    /** True if any blocking (Error-severity) form validation errors exist anywhere in the slice.
     *  Same predicate as the customization-page toolbar uses, so the two pages agree on Save state.
     *  When this is true and Save is therefore disabled, an accessible tooltip on the Save button +
     *  a visible icon next to it explain that validation errors block save and that the offending
     *  item may be hidden by current filters or only visible on the customization page. */
    hasFormValidationErrors: boolean,
    isContentAvailable: boolean,
    concurrentSaveError?: ConcurrentSaveError | null,
    /** Return TRUE if save was succesful, false otherwise. */
    onSave: (force?: boolean) => boolean,
    /** Return TRUE if undo was succesful, false otherwise. */
    onUndo: () => boolean,
    /** Return TRUE if single undo was succesful, false otherwise. */
    onSingleUndo: (scope: ModelSelectionScope, id: string) => boolean,
    customizationFetchError: string | null,
    /** Customization-base ids that have at least one Error-severity *form* validation error
     *  among their descendants. Drives the per-tab error icon. */
    customizationBaseIdsWithFormValidationErrors: ReadonlySet<string>,
    /** Customization-base ids that have at least one *backend* validation error keyed to a
     *  descendant entity (e.g. from a failed save round-trip). Same visual treatment as the
     *  form-error set above; the two are unioned at the tab-marking layer. */
    customizationBaseIdsWithBackendValidationErrors: ReadonlySet<string>,
    modelCustomizationSaveError: string | null,
    tagsMappedToCustomizations: Record<string, string[]>,
}

const ModelSelectionPage = (props: ModelSelectionPageProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { modelType, isSavingInProgress, onSave, onUndo, onSingleUndo,
        customizationBaseIdsInOrder, customizationBaseEntities, activeVersionTags, modelEntities,
        isAnyCustomizationModelModified, hasFormValidationErrors, isContentAvailable,
        customizationFetchError, customizationBaseIdsWithFormValidationErrors, customizationBaseIdsWithBackendValidationErrors,
        modelCustomizationSaveError, tagsMappedToCustomizations,
        concurrentSaveError,
    } = props;

    const currentTarget = useSelector(configurationTargetSelectors.selectCurrent);
    const appConfig = useSelector(appConfigSelectors.selectAppConfig);

    const [activeModelTab, setActiveModelTab] = useState('');
    
    // Get search text from the appropriate slice based on model type
    const searchText = useSelector((state: StoreState) => getActiveFreeTextFilter(state, modelType));

    const handleSearchTextChange = useCallback((value: string) => {
        const actionCreator = getFreeTextFilterAction(modelType);
        dispatch(actionCreator(value));
    }, [dispatch, modelType]);
    
    const [isSaveModalOpen, setSaveModalOpen] = useState(false);
    const [undoState, setUndoState] = useState<ModelSelectionUndoState>({ isModalOpen: false, scope: ModelSelectionScope.None, id: undefined });
    const [isUndoConfirmationModalOpen, setUndoConfirmationModalOpenState] = useState<boolean>(false);
    const concurrentError = useConcurrentSaveError(concurrentSaveError);

    const handleCloseUndoModal = useCallback(() => setUndoState({ isModalOpen: false, scope: ModelSelectionScope.None, id: undefined }), []);
    const handleOpenSaveDialog = useCallback(() => { setSaveModalOpen(true); concurrentError.undismiss(); }, [concurrentError]);
    const handleCloseSaveDialog = useCallback(() => { setSaveModalOpen(false); concurrentError.dismiss(); }, [concurrentError]);

    const handleOpenUndoDialog = useCallback(() => {
        setUndoConfirmationModalOpenState(true);
    }, []);

    const handleCloseUndoDialog = useCallback(() => {
        setUndoConfirmationModalOpenState(false);
    }, []);

    const handleUndo = useCallback(() => {
        if (onUndo()) {
            handleCloseUndoDialog();
        }
    }, [handleCloseUndoDialog, onUndo]);

    const handleSave = useCallback(() => {
        if (onSave(concurrentError.forceOverwrite)) {
            handleCloseSaveDialog();
        }
    }, [handleCloseSaveDialog, onSave, concurrentError.forceOverwrite]);

    const handleCancelSave = useCallback(() => {
        handleCloseSaveDialog();
    }, [handleCloseSaveDialog]);

    const handleTabChange = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>, eventKey: string | number) => {
        if (isString(eventKey)) {
            jumpToHeading(getHeadingForModelName(eventKey));
            setActiveModelTab(eventKey);
        }
    }, []);

    const handleSingleUndo = useCallback((scope: ModelSelectionScope, id: string) => {
        if (onSingleUndo(scope, id)) {
            handleCloseUndoModal();
        }
    }, [handleCloseUndoModal, onSingleUndo]);

    useEffect(() => {
        if (undoState.isModalOpen && undoState.id !== undefined) {
            handleSingleUndo(undoState.scope, undoState.id);
        }
    }, [handleSingleUndo, undoState]);

    const getUndoModalTitle = (undoScope: ModelSelectionScope) => {
        switch (undoScope) {
            case ModelSelectionScope.All:
                return 'modelSelection.undo.title.all';
            case ModelSelectionScope.ModelRules:
                return 'modelSelection.undo.title.modelRules';
            case ModelSelectionScope.AeTitleRule:
                return 'modelSelection.undo.title.aeTitleRule';
            case ModelSelectionScope.DicomRule:
                return 'modelSelection.undo.title.dicomRule';
            case ModelSelectionScope.DicomAttributeRule:
                return 'modelSelection.undo.title.dicomAttributeRule';
            case ModelSelectionScope.None:
            default:
                return 'modelSelection.undo.title.default';
        }
    }

    const getSelectionRulesForm = useCallback((customizationBaseId: string) => {
        switch (modelType) {
            case ModelType.Contouring:
                return (
                    <ContourSelectionRules
                        key={customizationBaseId}
                        customizationBaseId={customizationBaseId}
                        setUndoState={setUndoState}
                        heading={getHeadingForModelRules(customizationBaseId)}
                        showMultipleOutputsWarning={false}
                    />
                );
            case ModelType.Dose:
                return (
                    <DoseSelectionRules
                        key={customizationBaseId}
                        customizationBaseId={customizationBaseId}
                        setUndoState={setUndoState}
                        heading={getHeadingForModelRules(customizationBaseId)}
                        showMultipleOutputsWarning={false}
                    />
                );
            default:
                throw new Error(`Unsupported model type`);
        }
    }, [modelType]);

    if (modelType !== ModelType.Contouring && modelType !== ModelType.Dose) {
        throw new Error(`Unsupported model type for model selection page (${modelType})`);
    }

    const isSaveModalActuallyOpen = isSaveModalOpen || isSavingInProgress || concurrentError.isActive;
    const isSaveEnabled = !isSaveModalActuallyOpen && isAnyCustomizationModelModified && !hasFormValidationErrors;
    // Tooltip + warning icon next to the disabled Save button only kick in when the user actually
    // has unsaved changes that validation is blocking — without `isAnyCustomizationModelModified`
    // we'd loudly explain "validation errors" the moment a page loads with an invalid baseline,
    // even though there's nothing for the user to save yet.
    const showValidationBlockedHint = isAnyCustomizationModelModified && hasFormValidationErrors;
    const isUndoEnabled = !isSaveModalActuallyOpen && isAnyCustomizationModelModified;

    const filteredCustomizationBaseIds: CustomizationBaseWithModelName[] = useMemo(() => {
        return customizationBaseIdsInOrder.filter(c => {
            // Filter by search text (locale-aware, accent-sensitive)
            if (searchText && !localeIncludes(c.label, searchText)) { return false; }

            // Filter by version tags
            if (activeVersionTags.length > 0 && tagsMappedToCustomizations) {
                const customizationVersionTags = tagsMappedToCustomizations[c.customizationBaseId] || [];
                const hasMatchingVersionTag = activeVersionTags.some(activeTag => 
                    customizationVersionTags.includes(activeTag)
                );
                if (!hasMatchingVersionTag) { return false; }
            }

            return true;
        });
    }, [customizationBaseIdsInOrder, searchText, activeVersionTags, tagsMappedToCustomizations]);

    // Memoize tab rendering to prevent unnecessary re-renders
    const tabs = useMemo(() => {
        return filteredCustomizationBaseIds.map((c) => getTab({
            label: c.label,
            customization: customizationBaseEntities[c.customizationBaseId],
            customizationBaseIdsWithFormValidationErrors,
            customizationBaseIdsWithBackendValidationErrors,
            modelEntities,
            activeVersionTags,
        }));
    }, [filteredCustomizationBaseIds, customizationBaseEntities, customizationBaseIdsWithFormValidationErrors, customizationBaseIdsWithBackendValidationErrors, modelEntities, activeVersionTags]);

    // Memoize selection rules forms to prevent unnecessary re-renders
    const selectionRulesForms = useMemo(() => {
        return filteredCustomizationBaseIds.map(c => getSelectionRulesForm(c.customizationBaseId));
    }, [filteredCustomizationBaseIds, getSelectionRulesForm]);

    return (
        <>
            {appConfig && appConfig.showErrorsInUi && customizationFetchError !== null ? (
                <PageSection variant={PageSectionVariants.light} className="selectable">
                    <EmptyState>
                        <EmptyStateHeader titleText="An error occurred" headingLevel="h4" />
                        <EmptyStateBody>
                            <Trans i18nKey="selectionPage.customizationFetchError">
                                An error occurred when trying to load customization page: {{ customizationFetchError: customizationFetchError }}
                            </Trans>
                        </EmptyStateBody>
                    </EmptyState>
                </PageSection>
            ) : isContentAvailable ? (
                <PageSection variant={PageSectionVariants.light} padding={{ default: 'noPadding' }}>
                    <div className="selection-page-split">
                        <div className="selection-page-sidebar">
                            <div className="selection-search-bar">
                                <CustomizationSearchBar value={searchText} onChange={handleSearchTextChange} />
                            </div>
                            <div className="selection-filters mv-compact-tabs">
                                <CustomizationFilters modelType={modelType} />
                            </div>

                            <PageSection variant={PageSectionVariants.light}>
                                <div className="page-nav" data-testid="model-selection-tabs">
                                    <Tabs isVertical onSelect={handleTabChange} activeKey={activeModelTab} className="compact">
                                        {tabs}
                                    </Tabs>
                                </div>
                            </PageSection>
                        </div>

                        <div className="selection-page-content">
                            <PageSection variant={PageSectionVariants.light}>

                                <div className="model-selection-main-toolbar" id="model-selection-main-toolbar">
                                    {appConfig && appConfig.showErrorsInUi && modelCustomizationSaveError !== null && (
                                        <div className="toolbar-error selectable">
                                            <Alert variant="danger" isInline title="An error occurred when trying to save model selection">
                                                <p>{modelCustomizationSaveError}</p>
                                            </Alert>
                                        </div>
                                    )}
                                    <Toolbar>
                                        <ToolbarContent>
                                            <ToolbarGroup variant="button-group" >
                                                <ToolbarItem>
                                                    {/* When validation blocks save, the button is `aria-disabled` (not native `disabled`) so
                                                        it stays focusable — that lets the wrapping <Tooltip> wire its `aria-describedby` to the
                                                        button itself, and makes the embedded warning icon a normal part of the button rather
                                                        than a separate adjacent element. No `onClick` is needed here: this branch only renders
                                                        when validation has already disabled save, so the click should be a no-op. */}
                                                    {showValidationBlockedHint ? (
                                                        <Tooltip content={t('selectionPage.saveDisabledByValidationErrors')}>
                                                            <Button
                                                                variant="primary"
                                                                icon={<SaveIcon />}
                                                                data-testid="model-selection-save-all-changes-button"
                                                                isAriaDisabled
                                                            >
                                                                {t('common.saveAllChanges')}
                                                                <ExclamationCircleIcon
                                                                    className="model-selection-save-blocked-icon"
                                                                    data-testid="model-selection-save-blocked-icon"
                                                                />
                                                            </Button>
                                                        </Tooltip>
                                                    ) : (
                                                        <Button
                                                            variant="primary"
                                                            icon={<SaveIcon />}
                                                            data-testid="model-selection-save-all-changes-button"
                                                            isDisabled={!isSaveEnabled}
                                                            onClick={handleOpenSaveDialog}>{t('common.saveAllChanges')}</Button>
                                                    )}
                                                </ToolbarItem>
                                                <ToolbarItem>
                                                    <Button
                                                        variant="primary"
                                                        icon={<UndoIcon />}
                                                        isDisabled={!isUndoEnabled}
                                                        // className="revert-changes-button"
                                                        onClick={handleOpenUndoDialog}>{t('common.undoAllChanges')}</Button>
                                                </ToolbarItem>
                                            </ToolbarGroup>
                                        </ToolbarContent>
                                    </Toolbar>
                                </div>

                                <DataList aria-label={`Model selection configuration`} isCompact>
                                    {selectionRulesForms}
                                </DataList>

                            </PageSection>
                        </div>
                    </div>
                </PageSection>) : (
                <PageSection variant={PageSectionVariants.light}>
                    <EmptyState>
                        <EmptyStateHeader titleText={<>{t('selectConfigurationTarget.title')}</>} headingLevel="h4" />
                        <EmptyStateBody>{t('selectConfigurationTarget.message')}</EmptyStateBody>
                    </EmptyState>
                </PageSection>
            )}

            <Modal
                variant={ModalVariant.large}
                title={`Save current model selection configuration${hasUserName(currentTarget) ? ` for ${currentTarget!.userName}` : ''}?`}
                isOpen={isSaveModalActuallyOpen}
                onClose={handleCancelSave}
                actions={[
                    <Button key="confirmSave" variant="secondary" isLoading={isSavingInProgress} isDisabled={concurrentSaveError != null && !concurrentError.forceOverwrite} onClick={handleSave}>{t('common.saveAll')}</Button>,
                    <Button key="cancel" disabled={isSavingInProgress} variant="tertiary" onClick={handleCancelSave}>{t('common.cancel')}</Button>
                ]}
            >
                <Trans
                    i18nKey="selectionPage.save.confirm.text.saveModelSelection"
                    values={{
                        userName: hasUserName(currentTarget) ? currentTarget!.userName : ''
                    }}
                >
                    Do you want to overwrite existing model selection configuration for with your changes? Any
                    previous selection configurations are lost. Note that you can always revert back to default settings. <strong>Note: this operation will be performed both for your selection and customization changes!</strong>
                </Trans>
                <ConcurrentSaveErrorAlert
                    concurrentSaveError={concurrentSaveError}
                    forceOverwrite={concurrentError.forceOverwrite}
                    onForceOverwriteChange={concurrentError.setForceOverwrite}
                />
            </Modal>

            <Modal
                isOpen={isUndoConfirmationModalOpen}
                onClose={handleCloseUndoDialog}
                variant={ModalVariant.large}
                title={t(getUndoModalTitle(undoState.scope))}
                actions={[
                    <Button key="confirmUndo" variant="danger" onClick={handleUndo}>{t('common.undoAllChanges')}</Button>,
                    <Button key="cancelUndo" variant="secondary" onClick={handleCloseUndoDialog}>{t('common.keepChanges')}</Button>
                ]}
            >
                {getUndoModalText(undoState.scope)}
            </Modal>

        </>
    );
}

export default ModelSelectionPage;
